import React, { useCallback, useEffect, useRef, useState } from "react";
import Modal from "react-bootstrap/Modal";
import {
  COLOR_LIGHT_GRAY,
  COLOR_MID_GRAY,
  MODAL_SCROLL_OFFSET,
  UNIT,
} from "../../../../../../constants/configuration";
import { isChatbot } from "../../../../../../utils";
import CloseButton from "../../../../../CloseButton";
import ModalBorder from "../../../../../ModalBorder";
import { getProductData } from "./ProductModal.services";
import styles from "./ProductModal.styles";
import { useAppSelector } from "../../../../../../redux/hook";
import ProductModalBody from "./components/ProductModalBody";
import ProductModalFooter from "./components/ProductModalFooter";
import { resetHandledItems } from "../../../../../../redux/actions/productsAction";
import store from "../../../../../../redux/store";
import ProductOfferTooltip, {
  ProductOfferTooltipSize,
} from "../ProductOfferTooltip/ProductOfferTooltip";
import { rehydrateBngmItemsFromCart } from "../../../../../../utils/bngmOffer";
const { modalOptionContainer } = styles(COLOR_MID_GRAY);

export default function ProductModal({
  product,
  productOffer,
  title,
  image,
  price,
  description,
  categories,
  sectionName,
  showModal,
  productKey,
  sectionKey,
  onHideModal,
}) {
  const storeShopCategories = useAppSelector(
    (state) => state.shopReducer.categories,
  );
  const { commande } = useAppSelector((state) => state.pendingOrderReducer);

  const [nb, setNb] = useState(0);
  const [items, setItems] = useState({});
  const [instruction, setInstruction] = useState("");
  const [productCategories, setProductCategories] = useState([]);
  const [mandatoryCategories, setMandatoryCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [productAdded, setProductAdded] = useState(false);
  const [showOptionScrollButton, setShowOptionScrolButton] = useState(false);
  const [unit, setUnit] = useState(null);
  const [stepOptionIndex, setStepOptionIndex] = useState(0);
  const [itemsCounter, setItemsCounter] = useState({});
  const [modalHeight, setModalHeight] = useState(null);
  const [linkBetweenOptionsAndSubOptions, setLinkBetweenOptionsAndSubOptions] =
    useState(new Map());
  const [shopCategories, setShopCategories] = useState({});
  const prevShowModal = useRef(false);

  const initializeProductData = useCallback(() => {
    return getProductData(storeShopCategories, product, categories);
  }, [storeShopCategories, product, categories]);

  useEffect(() => {
    if (!showModal) return;

    const {
      unit,
      productCategories,
      mandatoryCategories,
      shopCategories: updatedShopCategories,
    } = initializeProductData();

    setUnit(unit);
    setNb(unit ? 0 : 1);
    setProductCategories(productCategories);
    setMandatoryCategories(mandatoryCategories);
    setShopCategories(updatedShopCategories);

    store.dispatch(resetHandledItems());
  }, [productKey, showModal]);

  const resetComponent = useCallback(
    (notHide = false) => {
      if (!notHide) {
        onHideModal();
      }

      const {
        unit,
        productCategories,
        mandatoryCategories,
        shopCategories: updatedShopCategories,
      } = initializeProductData();

      setNb(unit ? 0 : 1);
      setItems({});
      setInstruction("");
      setProductCategories(productCategories);
      setMandatoryCategories(mandatoryCategories);
      setLoading(false);
      setProductAdded(false);
      setShowOptionScrolButton(false);
      setUnit(unit);
      setStepOptionIndex(0);
      setItemsCounter({});
      setModalHeight(null);
      setLinkBetweenOptionsAndSubOptions(new Map());
      setShopCategories(updatedShopCategories);
    },
    [initializeProductData, onHideModal],
  );

  useEffect(() => {
    const justOpened = showModal && !prevShowModal.current;
    prevShowModal.current = showModal;
    if (!justOpened) {
      return;
    }
    if (!product?.isBngmOffer || !product?.bngmConfig) {
      return;
    }

    const bngmConfig = product.bngmConfig;
    const optionListCategory = storeShopCategories?.[bngmConfig.optionListKey];
    const { items: rehydrated, found } = rehydrateBngmItemsFromCart(
      commande,
      bngmConfig,
      optionListCategory,
    );

    if (found && rehydrated.length > 0) {
      const rehydratedItems = {};
      rehydrated.forEach((item, idx) => {
        rehydratedItems[idx] = item;
      });
      setItems(rehydratedItems);
    }
  }, [showModal, product, commande, storeShopCategories]);

  useEffect(() => {
    if (product?.disabled) {
      resetComponent();
      return;
    }
  }, [product?.disabled]);

  useEffect(() => {
    const modalBody = document.getElementById("product-modal-body");
    const modalBorder = document.getElementById("product-modal-border");

    if (!modalBody || !modalBorder || modalHeight) {
      return;
    }

    const shouldShowScroll =
      modalBody.offsetHeight > modalBorder.offsetHeight + MODAL_SCROLL_OFFSET;

    if (shouldShowScroll) {
      setShowOptionScrolButton(true);
      setModalHeight(modalBody.offsetHeight);
    }
  }, [modalHeight]);

  const handleScroll = () => {
    const modalBorder = document.getElementById("product-modal-border");
    const modalBody = document.getElementById("product-modal-body");
    const modalTitle = document.getElementById("product-modal-title");

    if (!modalBorder || !modalBody || !modalTitle) {
      return;
    }

    const shouldShow =
      modalBorder.scrollTop < modalTitle.offsetHeight &&
      modalBody.offsetHeight > modalBorder.offsetHeight + MODAL_SCROLL_OFFSET;

    setShowOptionScrolButton(shouldShow);
  };

  return (
    <Modal
      show={showModal}
      onHide={() => resetComponent()}
      aria-labelledby="contained-modal-title-vcenter"
      scrollable
      centered
    >
      {productOffer && (
        <ProductOfferTooltip
          offer={productOffer}
          size={ProductOfferTooltipSize.MEDIUM}
        />
      )}

      <ModalBorder
        className="hide-scroll modal-size-fixed"
        color={COLOR_LIGHT_GRAY}
        id="product-modal-border"
        onScroll={handleScroll}
      >
        <div>
          <CloseButton
            closeStyle={{ right: UNIT * 0.5 }}
            onClick={() => resetComponent()}
          />
          <Modal.Body className="p-3 dark-modal" id="product-modal-body">
            <ProductModalBody
              product={product}
              productOffer={productOffer}
              productAdded={productAdded}
              title={title}
              image={image}
              description={description}
              items={items}
              nb={nb}
              unit={unit}
              productCategories={productCategories}
              stepOptionIndex={stepOptionIndex}
              shopCategories={shopCategories}
              modalOptionContainer={modalOptionContainer}
              itemsCounter={itemsCounter}
              mandatoryCategories={mandatoryCategories}
              linkBetweenOptionsAndSubOptions={linkBetweenOptionsAndSubOptions}
              setItems={setItems}
              setShopCategories={setShopCategories}
              setProductCategories={setProductCategories}
              setMandatoryCategories={setMandatoryCategories}
              setLinkBetweenOptionsAndSubOptions={
                setLinkBetweenOptionsAndSubOptions
              }
              setItemsCounter={setItemsCounter}
              instruction={instruction}
              setInstruction={setInstruction}
              setNb={setNb}
              setUnit={setUnit}
              loading={loading}
              price={price}
              setLoading={setLoading}
              sectionName={sectionName}
              sectionKey={sectionKey}
              resetComponent={resetComponent}
              productKey={productKey}
            />
          </Modal.Body>
          {!isChatbot() && (
            <Modal.Footer
              className="footer-color modal-sticky-footer px-3"
              style={{ height: "60px" }}
            >
              <ProductModalFooter
                productAdded={productAdded}
                items={items}
                shopCategories={shopCategories}
                price={price}
                nb={nb}
                unit={unit}
                product={product}
                productOffer={productOffer}
                setLoading={setLoading}
                mandatoryCategories={mandatoryCategories}
                productCategories={productCategories}
                productKey={productKey}
                instruction={instruction}
                sectionName={sectionName}
                sectionKey={sectionKey}
                resetComponent={resetComponent}
                stepOptionIndex={stepOptionIndex}
                loading={loading}
                setStepOptionIndex={setStepOptionIndex}
                setShowOptionScrolButton={setShowOptionScrolButton}
                showOptionScrollButton={showOptionScrollButton}
              />
            </Modal.Footer>
          )}
        </div>
      </ModalBorder>
    </Modal>
  );
}
